import odsButtonHref from "@telekom-ods/react-ui-kit/ODSButton.css?url";
import odsLoadingSpinnerHref from "@telekom-ods/react-ui-kit/ODSLoadingSpinner.css?url";
import odsRowHref from "@telekom-ods/react-ui-kit/ODSRow.css?url";
import odsIndexHref from "@telekom-ods/react-ui-kit/style.css?url";
import widgetCssHref from "@/widget.css?url";

const LINK_ATTR = "data-telekomId-ods-stylesheet";

/**
 * Base URL of the directory that contains `telekomid-widget.js` (trailing slash).
 * Vite's default `?url` resolution uses `document.currentScript` at **parse time**; that is
 * `null` for `defer` / `async` scripts, so URLs would fall back to `document.baseURI` (the
 * channel page) and ODS `<link>` stylesheets would 404. Resolving against `#md-login`'s
 * `src` fixes production embeds.
 */
function getWidgetScriptBaseUrl(): string {
  const el = document.getElementById("md-login");
  if (el instanceof HTMLScriptElement && el.src) {
    return new URL("./", el.src).href;
  }

  const cs = document.currentScript;
  if (cs instanceof HTMLScriptElement && cs.src) {
    return new URL("./", cs.src).href;
  }

  return new URL("./", document.baseURI).href;
}

/** Pull `assets/<name>-<hash>.css` from a bundler-produced URL (any origin). */
function extractViteAssetsCssPath(href: string): string | null {
  const m = href.match(/(assets\/[^"'?]+\.css)(?:\?|$)/);
  return m ? m[1] : null;
}

function resolveShadowStylesheetHref(hrefFromBundler: string): string {
  if (import.meta.env.DEV) return hrefFromBundler;

  const rel = extractViteAssetsCssPath(hrefFromBundler);
  if (!rel) return hrefFromBundler;

  return new URL(rel, getWidgetScriptBaseUrl()).href;
}

/**
 * ODS styles scoped to the shadow host via `vite-plugin-ods-shadow-css`
 * (`html` / `:root` / theme attrs → `:host`).
 *
 * Order: design tokens first, widget base styles, then ODSButton dependencies.
 */
const ODS_SHADOW_STYLESHEETS: readonly string[] = [
  odsIndexHref,
  widgetCssHref,
  odsRowHref,
  odsLoadingSpinnerHref,
  odsButtonHref,
];

/**
 * Append ODS and widget `<link rel="stylesheet">` nodes into the shadow tree once.
 * Nothing is linked from `document.head`, so ODS typography/tokens cannot leak to
 * the host page.
 */
export const ensureOdsStylesInShadow = (shadow: ShadowRoot): void => {
  if (shadow.querySelector(`link[${LINK_ATTR}]`)) return;

  const resolvedHrefs = ODS_SHADOW_STYLESHEETS.map((href) =>
    resolveShadowStylesheetHref(href),
  );

  const frag = document.createDocumentFragment();
  for (const href of resolvedHrefs) {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    link.setAttribute(LINK_ATTR, "");
    frag.appendChild(link);
  }
  shadow.insertBefore(frag, shadow.firstChild);
};
